import React, { useEffect } from 'react';
import { Box, Typography, IconButton, Divider } from '@mui/material';
import { CloseOutlined } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';

const PRIMARY = '#1075BA';
const PANEL_WIDTH = 480;

/**
 * SlidePanel
 * A drawer that slides in from the right over a dimmed backdrop.
 *
 * Props:
 *   open       – boolean
 *   onClose    – fn
 *   title      – string
 *   subtitle   – string (optional)
 *   children   – form content
 *   footer     – node rendered in a sticky bottom bar (save/cancel buttons)
 *   width      – number (default 480)
 */
export default function SlidePanel({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  width = PANEL_WIDTH,
}) {
  // Close on Escape key
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Prevent body scroll when open
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  return (
    <>
      {/* Backdrop */}
      <Box
        onClick={onClose}
        sx={{
          position: 'fixed', inset: 0, zIndex: 1200,
          background: 'rgba(15, 33, 55, 0.45)',
          backdropFilter: 'blur(2px)',
          opacity: open ? 1 : 0,
          pointerEvents: open ? 'auto' : 'none',
          transition: 'opacity 0.28s ease',
        }}
      />

      {/* Panel */}
      <Box
        sx={{
          position: 'fixed', top: 0, right: 0, bottom: 0,
          width: { xs: '100vw', sm: width },
          zIndex: 1300,
          display: 'flex', flexDirection: 'column',
          background: '#fff',
          boxShadow: '-8px 0 40px rgba(15,33,55,0.18)',
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.32s cubic-bezier(0.4, 0, 0.2, 1)',
          borderLeft: `1px solid ${alpha(PRIMARY, 0.1)}`,
        }}
      >
        {/* Header */}
        <Box sx={{
          px: 3, pt: 3, pb: 2.5,
          borderBottom: `1px solid ${alpha(PRIMARY, 0.09)}`,
          background: `linear-gradient(135deg, ${alpha(PRIMARY, 0.03)} 0%, transparent 100%)`,
          flexShrink: 0,
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2,
        }}>
          <Box>
            <Typography sx={{
              fontWeight: 800, fontSize: '1.1rem', color: 'text.primary',
              lineHeight: 1.2, fontFamily: '"Nunito", sans-serif',
            }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.4, display: 'block' }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              mt: -0.5, flexShrink: 0,
              color: 'text.secondary',
              border: `1px solid ${alpha(PRIMARY, 0.15)}`,
              borderRadius: 2,
              '&:hover': { background: alpha(PRIMARY, 0.06), color: 'text.primary' },
            }}
          >
            <CloseOutlined sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        {/* Scrollable body */}
        <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2.5,
          '&::-webkit-scrollbar': { width: 5 },
          '&::-webkit-scrollbar-track': { background: '#f0f4f8' },
          '&::-webkit-scrollbar-thumb': { background: alpha(PRIMARY, 0.25), borderRadius: 3 },
        }}>
          {children}
        </Box>

        {/* Sticky footer */}
        {footer && (
          <Box sx={{
            px: 3, py: 2,
            borderTop: `1px solid ${alpha(PRIMARY, 0.09)}`,
            background: '#fafcff',
            flexShrink: 0,
            display: 'flex', gap: 1.5, justifyContent: 'flex-end',
          }}>
            {footer}
          </Box>
        )}
      </Box>
    </>
  );
}